/**
 * Centralized API endpoints configuration for the application.
 *
 * Base URLs are loaded from environment variables:
 * - REACT_APP_AUTH_URI → Users API
 * - REACT_APP_GNSS_API_URI → GNSS API
 */
const API_ENDPOINTS = {
  /**
   * Users & Authentication API
   */
  usersApi: {
    baseUrl: `${process.env.REACT_APP_AUTH_URI}`,
    endpoints: {
      self: {
        leaveTenant: (tenantId) => `/self/tenants/${tenantId}`,
        updateProfile: `/self/profile`,
        updatePassword: `/self/password`,
        notificationsEnabled: (enabled) =>
          `/self/notifications?enabled=${enabled}`,
      },
      tenants: {
        owners: {
          /** Register a new owner for a tenant */
          postRegister: (tenantId) => `/tenants/${tenantId}/owners/register`,
          /** Verify tenant owner register token */
          postRegisterToken: (tenantId) =>
            `/tenants/${tenantId}/owners/register/token`,
        },
        users: {
          /** Get all users for a tenant */
          getAll: (tenantId) => `/tenants/${tenantId}/users`,
          /** Get user profile by tenantId and userId */
          getProfile: (tenantId, userId) =>
            `/tenants/${tenantId}/users/${userId}/profile`,
          /** Delete user by tenantId and userId */
          deleteById: (tenantId, userId) =>
            `/tenants/${tenantId}/users/${userId}`,
          /** Update user profile */
          updateProfile: (tenantId, userId) =>
            `/tenants/${tenantId}/users/${userId}/profile`,
          /** Update user role in a tenant*/
          updateRole: (tenantId, userId) =>
            `/tenants/${tenantId}/users/${userId}/role`,
          /** Update user’s default tenant */
          updateDefaultTenant: (tenantId, userId) =>
            `/tenants/${tenantId}/users/${userId}/default-tenant`,
          /** Send user invitation */
          postInvitation: (tenantId) => `/tenants/${tenantId}/users/invitation`,
          /** Register a new user for a tenant */
          postRegister: (tenantId) => `/tenants/${tenantId}/users/register`,
          /** Verify user register token */
          postRegisterToken: (tenantId) =>
            `/tenants/${tenantId}/users/register/token`,
        },
        groups: {
          /** Get all groups for a tenant */
          getAll: (tenantId) => `/tenants/${tenantId}/groups`,
          /** Get group details with optional limit */
          getDetails: (tenantId, limit = 0) =>
            `/tenants/${tenantId}/groups/details?${
              limit > 0 ? `&limit=${limit}` : ""
            }`,
          /** Create a new group */
          create: (tenantId) => `/tenants/${tenantId}/groups`,
          /** Delete a group by ID */
          delete: (tenantId, groupId) =>
            `/tenants/${tenantId}/groups/${groupId}`,
          /** Add a user to a group */
          addUser: (tenantId, groupId) =>
            `/tenants/${tenantId}/groups/${groupId}/users`,
          /** Update a user’s role inside a group */
          updateUserRole: (tenantId, groupId, userId) =>
            `/tenants/${tenantId}/groups/${groupId}/users/${userId}/role`,
          /** Remove a user from a group */
          deleteUser: (tenantId, groupId) =>
            `/tenants/${tenantId}/groups/${groupId}/users`,
        },
      },
      /** Get available roles */
      getRoles: `/roles`,
      /** Start password recovery flow */
      recoverPassword: `/users/password/recovery`,
      verifyRecoverToken: `/users/password/recovery/token`,
    },
  },
  /**
   * GNSS API
   */
  gnssApi: {
    baseUrl: `${process.env.REACT_APP_GNSS_API_URI}`,
    endpoints: {
      stations: {
        /** Get all station details */
        getAll: `/stations/details`,
        /** Get station details by groupName and stationId */
        getById: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/details`,
        /** Add a new station */
        add: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/details`,
        /** Update station details */
        update: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/details`,
        /** Update station level */
        updateLevel: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/details/level`,
        /** Delete station by groupName and stationId */
        deleteById: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/details`,
      },
      periodic_reports: {
        /** Get latest periodic report */
        getLast: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/reports/periodic/latest`,
        /** Get last report date */
        getLastDate: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/reports/periodic/lastDate`,
        /** Get all report dates */
        getAllDates: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/reports/periodic/dates`,
        /** Get periodic report by date */
        getByDate: (groupName, stationId, date) =>
          `/stations/${groupName}/${stationId}/reports/periodic?date=${date}`,
        /** Add a periodic report */
        add: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/reports/periodic`,
        /** Add Stanford plots for a station */
        addStanfordPlots: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/reports/periodic/stanfordplots`,
        /** Delete a periodic report by date */
        deleteByDate: (groupName, stationId, date) =>
          `/stations/${groupName}/${stationId}/reports/periodic?date=${date}`,
      },
      realtime_reports: {
        getSpectrogram: (groupName, stationId, targetWidth, targetHeight) =>
          `/stations/${groupName}/${stationId}/reports/realtime/image?targetWidth=${targetWidth}&targetHeight=${targetHeight}&yMin=0&yRange=60`,
        getAlertsIds: (groupName, stationId, date) => {
          const query = date ? `?date=${encodeURIComponent(date)}` : "";
          return `/stations/${groupName}/${stationId}/reports/realtime/alerts${query}`;
        },
        getAlerts: (groupName, stationId, limit) =>
          `/stations/${groupName}/${stationId}/reports/realtime/alerts/details?limit=${limit}`,
        getAlertsFiltered: (groupName, stationId, filters) => {
          const query = new URLSearchParams(filters).toString();
          return `/stations/${groupName}/${stationId}/reports/realtime/alerts/details?${query}`;
        },
        getAlert: (groupName, stationId, alertId) =>
          `/stations/${groupName}/${stationId}/reports/realtime/alerts/${alertId}/details`,
        getAlertAnalytics: (groupName, stationId, alertId) =>
          `/stations/${groupName}/${stationId}/reports/realtime/alerts/${alertId}/analytics?threshold=500`,
        getAllAnalytics: (groupName, stationId) =>
          `/stations/${groupName}/${stationId}/reports/realtime/alerts/analytics/all`,
        getAnalyticsByDateInterval: (
          groupName,
          stationId,
          startDate,
          stopDate,
        ) =>
          `/stations/${groupName}/${stationId}/reports/realtime/alerts/analytics?startDate=${startDate}&stopDate=${stopDate}&threshold=500`,
      },
    },
  },
};

export default API_ENDPOINTS;
